import * as THREE from 'three';

export interface PanelState {
  preset: string;
  variableVelocity: boolean;
  legacyMotionTuned: boolean;
  quality: string;
  paused: boolean;
  particleGrid: number;
  colourScale: number;
  pointScale: number;
  animationSpeed: number;
  structureScale: number;
  distantBrightness: number;
  passthroughEnabled: boolean;
  passthroughSupported: boolean;
  passthroughActive: boolean;
  recordingSupported: boolean;
  recordingActive: boolean;
  recordingElapsedSeconds: number;
  recordingNotice: string;
  tracerTrailsEnabled: boolean;
  gravityMarkersEnabled: boolean;
  audioEnabled: boolean;
  velocityAudioEnabled: boolean;
  audioMode: 'musical' | 'scientific';
  velocityTimbre: 'sine' | 'softPad' | 'brightPad' | 'deepSpace' | 'choir' | 'shimmer' | 'glass' | 'darkDrone';
  distanceTimbre: 'warm' | 'choir' | 'shimmer' | 'deep' | 'sine';
  audioPalette: string;
  audioVolume: number;
  audioRange: number;
  audioResponse: number;
  musicalMovement: number;
  eventDensity: number;
  adaptiveAudioRanges: boolean;
  rareAccentsEnabled: boolean;
  spatialSourcesEnabled: boolean;
  distanceAudioEnabled: boolean;
  distanceAudioMix: number;
  distanceAudioRange: number;
  cullEnabled: boolean;
  cullDistance: number;
}

export type PanelAction = 'previous' | 'next' | 'pause' | 'reset' | 'quality' | 'recording' | 'exit'
  | 'particles' | 'colourScale' | 'pointScale' | 'animationSpeed' | 'structureScale' | 'distantBrightness'
  | 'passthrough' | 'tracerTrails' | 'gravityMarkers' | 'cullEnabled' | 'cullDistance' | 'audioPage' | 'soundDesignPage'
  | 'audioEnabled' | 'velocityAudioEnabled' | 'audioMusical' | 'audioScientific' | 'velocityTimbre' | 'audioVolume' | 'audioRange' | 'audioResponse' | 'musicalMovement'
  | 'distanceAudioEnabled' | 'distanceAudioMix' | 'distanceAudioRange' | 'distanceTimbre'
  | 'eventDensity' | 'adaptiveAudioRanges' | 'rareAccents' | 'spatialSources';

const BUTTONS: Array<{ action: PanelAction; x: number; y: number; w: number; h: number; label: string }> = [
  { action: 'previous', x: 52, y: 890, w: 210, h: 74, label: 'PREVIOUS' },
  { action: 'next', x: 286, y: 890, w: 210, h: 74, label: 'NEXT' },
  { action: 'pause', x: 52, y: 984, w: 210, h: 74, label: 'PAUSE' },
  { action: 'reset', x: 286, y: 984, w: 210, h: 74, label: 'RESTART' },
  { action: 'quality', x: 52, y: 1078, w: 96, h: 66, label: 'QUALITY' },
  { action: 'recording', x: 160, y: 1078, w: 96, h: 66, label: 'RECORD' },
  { action: 'audioPage', x: 268, y: 1078, w: 96, h: 66, label: 'AUDIO' },
  { action: 'exit', x: 376, y: 1078, w: 120, h: 66, label: 'EXIT XR' }
];

const PARTICLE_SLIDER = { x: 62, y: 253, w: 424, h: 54 };
const COLOUR_SLIDER = { x: 62, y: 333, w: 424, h: 54 };
const POINT_SLIDER = { x: 62, y: 413, w: 424, h: 54 };
const SPEED_SLIDER = { x: 62, y: 493, w: 424, h: 54 };
const STRUCTURE_SLIDER = { x: 62, y: 573, w: 424, h: 54 };
const DISTANT_BRIGHTNESS_SLIDER = { x: 62, y: 653, w: 424, h: 54 };
const TRAILS_TOGGLE = { x: 62, y: 742, w: 133, h: 50 };
const MARKERS_TOGGLE = { x: 207, y: 742, w: 133, h: 50 };
const PASSTHROUGH_TOGGLE = { x: 352, y: 742, w: 134, h: 50 };
const CULL_TOGGLE = { x: 62, y: 822, w: 132, h: 50 };
const CULL_SLIDER = { x: 220, y: 813, w: 266, h: 64 };
const AUDIO_TOGGLE = { x: 52, y: 190, w: 210, h: 70 };
const VELOCITY_AUDIO_TOGGLE = { x: 286, y: 190, w: 210, h: 70 };
const MUSICAL_BUTTON = { x: 52, y: 310, w: 132, h: 70 };
const SCIENTIFIC_BUTTON = { x: 208, y: 310, w: 132, h: 70 };
const VELOCITY_TIMBRE_BUTTON = { x: 364, y: 310, w: 132, h: 70 };
const AUDIO_VOLUME_SLIDER = { x: 62, y: 420, w: 424, h: 54 };
const AUDIO_RANGE_SLIDER = { x: 62, y: 510, w: 424, h: 54 };
const AUDIO_RESPONSE_SLIDER = { x: 62, y: 600, w: 424, h: 54 };
const MUSICAL_MOVEMENT_SLIDER = { x: 62, y: 690, w: 424, h: 54 };
const DISTANCE_AUDIO_TOGGLE = { x: 52, y: 770, w: 444, h: 54 };
const DISTANCE_AUDIO_MIX_SLIDER = { x: 62, y: 850, w: 424, h: 54 };
const DISTANCE_AUDIO_RANGE_SLIDER = { x: 62, y: 950, w: 424, h: 54 };
const AUDIO_BACK = { x: 52, y: 1078, w: 96, h: 66 };
const AUDIO_RECORD = { x: 160, y: 1078, w: 96, h: 66 };
const AUDIO_DESIGN = { x: 268, y: 1078, w: 96, h: 66 };
const AUDIO_EXIT = { x: 376, y: 1078, w: 120, h: 66 };
const DESIGN_VELOCITY_TIMBRE = { x: 52, y: 205, w: 444, h: 70 };
const DESIGN_DISTANCE_TIMBRE = { x: 52, y: 315, w: 444, h: 70 };
const DESIGN_ADAPTIVE = { x: 52, y: 430, w: 444, h: 62 };
const DESIGN_EVENT_DENSITY = { x: 62, y: 540, w: 424, h: 58 };
const DESIGN_SPATIAL = { x: 52, y: 640, w: 444, h: 62 };
const DESIGN_ACCENTS = { x: 52, y: 740, w: 444, h: 62 };
const DESIGN_BACK = { x: 52, y: 1078, w: 96, h: 66 };
const DESIGN_RECORD = { x: 160, y: 1078, w: 96, h: 66 };
const DESIGN_AUDIO = { x: 268, y: 1078, w: 96, h: 66 };
const DESIGN_EXIT = { x: 376, y: 1078, w: 120, h: 66 };

export class XRPanel extends THREE.Group {
  private readonly canvas = document.createElement('canvas');
  private readonly context: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly panel: THREE.Mesh;
  private page: 'simulation' | 'audio' | 'design' = 'simulation';
  private state: PanelState = {
    preset: '', variableVelocity: false, legacyMotionTuned: false, quality: '', paused: false, particleGrid: 256, colourScale: 25, pointScale: 8,
    animationSpeed: 1, structureScale: 1, distantBrightness: 1,
    passthroughEnabled: false, passthroughSupported: false,
    passthroughActive: false, recordingSupported: false, recordingActive: false,
    recordingElapsedSeconds: 0, recordingNotice: '',
    tracerTrailsEnabled: false, gravityMarkersEnabled: false,
    audioEnabled: false, velocityAudioEnabled: true,
    audioMode: 'musical', velocityTimbre: 'softPad', distanceTimbre: 'warm', audioPalette: 'Orbital',
    audioVolume: 0.3, audioRange: 25, audioResponse: 0.45, musicalMovement: 0.55,
    eventDensity: 0.65, adaptiveAudioRanges: true, rareAccentsEnabled: true, spatialSourcesEnabled: true,
    distanceAudioEnabled: true, distanceAudioMix: 0.45, distanceAudioRange: 1000,
    cullEnabled: true, cullDistance: 6000
  };

  constructor(private readonly onAction: (action: PanelAction, value?: number) => void) {
    super();
    this.canvas.width = 548;
    this.canvas.height = 1210;
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable.');
    this.context = context;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    const geometry = new THREE.PlaneGeometry(0.9, 1.987);
    const material = new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthTest: true });
    this.panel = new THREE.Mesh(geometry, material);
    this.panel.userData.xrPanel = this;
    this.add(this.panel);
    this.position.set(0, 1.45, -1.35);
    this.draw();
  }

  update(state: PanelState): void {
    this.state = state;
    this.draw();
  }

  hit(uv: THREE.Vector2): boolean {
    const x = uv.x * this.canvas.width;
    const y = (1 - uv.y) * this.canvas.height;
    if (this.page === 'audio') return this.hitAudio(x, y);
    if (this.page === 'design') return this.hitDesign(x, y);
    if (x >= PARTICLE_SLIDER.x && x <= PARTICLE_SLIDER.x + PARTICLE_SLIDER.w
      && y >= PARTICLE_SLIDER.y && y <= PARTICLE_SLIDER.y + PARTICLE_SLIDER.h) {
      const fraction = THREE.MathUtils.clamp((x - PARTICLE_SLIDER.x) / PARTICLE_SLIDER.w, 0, 1);
      const grid = Math.round(64 + fraction * (1224 - 64));
      this.onAction('particles', grid);
      return true;
    }
    if (x >= COLOUR_SLIDER.x && x <= COLOUR_SLIDER.x + COLOUR_SLIDER.w
      && y >= COLOUR_SLIDER.y && y <= COLOUR_SLIDER.y + COLOUR_SLIDER.h) {
      const fraction = THREE.MathUtils.clamp((x - COLOUR_SLIDER.x) / COLOUR_SLIDER.w, 0, 1);
      this.onAction('colourScale', Math.round(1 + fraction * 99));
      return true;
    }
    if (x >= POINT_SLIDER.x && x <= POINT_SLIDER.x + POINT_SLIDER.w
      && y >= POINT_SLIDER.y && y <= POINT_SLIDER.y + POINT_SLIDER.h) {
      const fraction = THREE.MathUtils.clamp((x - POINT_SLIDER.x) / POINT_SLIDER.w, 0, 1);
      this.onAction('pointScale', Math.round(2 + fraction * 18));
      return true;
    }
    if (x >= SPEED_SLIDER.x && x <= SPEED_SLIDER.x + SPEED_SLIDER.w
      && y >= SPEED_SLIDER.y && y <= SPEED_SLIDER.y + SPEED_SLIDER.h) {
      const fraction = THREE.MathUtils.clamp((x - SPEED_SLIDER.x) / SPEED_SLIDER.w, 0, 1);
      this.onAction('animationSpeed', Math.round((0.25 + fraction * 19.75) * 4) / 4);
      return true;
    }
    if (x >= STRUCTURE_SLIDER.x && x <= STRUCTURE_SLIDER.x + STRUCTURE_SLIDER.w
      && y >= STRUCTURE_SLIDER.y && y <= STRUCTURE_SLIDER.y + STRUCTURE_SLIDER.h) {
      const fraction = THREE.MathUtils.clamp((x - STRUCTURE_SLIDER.x) / STRUCTURE_SLIDER.w, 0, 1);
      this.onAction('structureScale', Math.round(structureScaleFromFraction(fraction) * 4) / 4);
      return true;
    }
    if (x >= DISTANT_BRIGHTNESS_SLIDER.x && x <= DISTANT_BRIGHTNESS_SLIDER.x + DISTANT_BRIGHTNESS_SLIDER.w
      && y >= DISTANT_BRIGHTNESS_SLIDER.y && y <= DISTANT_BRIGHTNESS_SLIDER.y + DISTANT_BRIGHTNESS_SLIDER.h) {
      const fraction = THREE.MathUtils.clamp((x - DISTANT_BRIGHTNESS_SLIDER.x) / DISTANT_BRIGHTNESS_SLIDER.w, 0, 1);
      this.onAction('distantBrightness', Math.round((1 + fraction * 5) * 4) / 4);
      return true;
    }
    if (x >= TRAILS_TOGGLE.x && x <= TRAILS_TOGGLE.x + TRAILS_TOGGLE.w
      && y >= TRAILS_TOGGLE.y && y <= TRAILS_TOGGLE.y + TRAILS_TOGGLE.h) {
      this.onAction('tracerTrails');
      return true;
    }
    if (x >= MARKERS_TOGGLE.x && x <= MARKERS_TOGGLE.x + MARKERS_TOGGLE.w
      && y >= MARKERS_TOGGLE.y && y <= MARKERS_TOGGLE.y + MARKERS_TOGGLE.h) {
      this.onAction('gravityMarkers');
      return true;
    }
    if (x >= PASSTHROUGH_TOGGLE.x && x <= PASSTHROUGH_TOGGLE.x + PASSTHROUGH_TOGGLE.w
      && y >= PASSTHROUGH_TOGGLE.y && y <= PASSTHROUGH_TOGGLE.y + PASSTHROUGH_TOGGLE.h) {
      if (this.state.passthroughSupported) this.onAction('passthrough');
      return true;
    }
    if (x >= CULL_TOGGLE.x && x <= CULL_TOGGLE.x + CULL_TOGGLE.w
      && y >= CULL_TOGGLE.y && y <= CULL_TOGGLE.y + CULL_TOGGLE.h) {
      this.onAction('cullEnabled');
      return true;
    }
    if (x >= CULL_SLIDER.x && x <= CULL_SLIDER.x + CULL_SLIDER.w
      && y >= CULL_SLIDER.y && y <= CULL_SLIDER.y + CULL_SLIDER.h) {
      if (this.state.cullEnabled) {
        const fraction = THREE.MathUtils.clamp((x - CULL_SLIDER.x) / CULL_SLIDER.w, 0, 1);
        this.onAction('cullDistance', Math.round((100 + fraction * 49900) / 100) * 100);
      }
      return true;
    }
    const button = BUTTONS.find((item) => x >= item.x && x <= item.x + item.w && y >= item.y && y <= item.y + item.h);
    if (!button) return false;
    if (button.action === 'audioPage') {
      this.page = 'audio';
      this.draw();
      return true;
    }
    this.onAction(button.action);
    return true;
  }

  get target(): THREE.Mesh { return this.panel; }

  private draw(): void {
    const ctx = this.context;
    const gradient = ctx.createLinearGradient(0, 0, 548, 1210);
    gradient.addColorStop(0, '#101833');
    gradient.addColorStop(1, '#060914');
    ctx.clearRect(0, 0, 548, 1210);
    ctx.fillStyle = gradient;
    this.roundRect(0, 0, 548, 1210, 32);
    ctx.fill();
    ctx.strokeStyle = 'rgba(88, 181, 255, .38)';
    ctx.lineWidth = 3;
    this.roundRect(2, 2, 544, 1206, 30);
    ctx.stroke();

    if (this.page === 'audio') {
      this.drawAudioPage();
      this.texture.needsUpdate = true;
      return;
    }
    if (this.page === 'design') {
      this.drawDesignPage();
      this.texture.needsUpdate = true;
      return;
    }

    ctx.fillStyle = '#71d7ff';
    ctx.font = '700 22px Arial';
    ctx.fillText('GSPACE  /  GRAVITY LAB', 52, 60);
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 38px Arial';
    ctx.fillText(this.state.preset, 52, 126, 444);
    ctx.fillStyle = '#98a6c8';
    ctx.font = '24px Arial';
    ctx.fillText(this.state.quality, 52, 174);
    if (this.state.variableVelocity) {
      ctx.fillStyle = '#ffcf70';
      ctx.font = '700 15px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(this.state.legacyMotionTuned ? 'VARIABLE VELOCITY · TUNED' : 'VARIABLE VELOCITY', 486, 174, 270);
      ctx.textAlign = 'left';
    }
    ctx.fillStyle = this.state.paused ? '#ffcf70' : '#72f0c0';
    ctx.font = '700 18px Arial';
    ctx.fillText(this.state.paused ? '●  PAUSED' : '●  LIVE SIMULATION', 52, 216);

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('PARTICLES', 62, 249);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText((this.state.particleGrid ** 2).toLocaleString(), 486, 249);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(PARTICLE_SLIDER.x, 280);
    ctx.lineTo(PARTICLE_SLIDER.x + PARTICLE_SLIDER.w, 280);
    ctx.stroke();
    const sliderFraction = (this.state.particleGrid - 64) / (1224 - 64);
    const knobX = PARTICLE_SLIDER.x + PARTICLE_SLIDER.w * sliderFraction;
    ctx.fillStyle = '#71d7ff';
    ctx.beginPath();
    ctx.arc(knobX, 280, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('VELOCITY COLOUR', 62, 329);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText(String(this.state.colourScale), 486, 329);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(COLOUR_SLIDER.x, 360);
    ctx.lineTo(COLOUR_SLIDER.x + COLOUR_SLIDER.w, 360);
    ctx.stroke();
    const colourFraction = (this.state.colourScale - 1) / 99;
    const colourKnobX = COLOUR_SLIDER.x + COLOUR_SLIDER.w * colourFraction;
    ctx.fillStyle = '#ff9b6a';
    ctx.beginPath();
    ctx.arc(colourKnobX, 360, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('PARTICLE SIZE', 62, 409);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText(String(this.state.pointScale), 486, 409);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(POINT_SLIDER.x, 440);
    ctx.lineTo(POINT_SLIDER.x + POINT_SLIDER.w, 440);
    ctx.stroke();
    const pointFraction = (this.state.pointScale - 2) / 18;
    const pointKnobX = POINT_SLIDER.x + POINT_SLIDER.w * pointFraction;
    ctx.fillStyle = '#f4f8ff';
    ctx.beginPath();
    ctx.arc(pointKnobX, 440, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('ANIMATION SPEED', 62, 489);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText(`${formatSpeed(this.state.animationSpeed)}×`, 486, 489);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(SPEED_SLIDER.x, 520);
    ctx.lineTo(SPEED_SLIDER.x + SPEED_SLIDER.w, 520);
    ctx.stroke();
    const speedFraction = (this.state.animationSpeed - 0.25) / 19.75;
    const speedKnobX = SPEED_SLIDER.x + SPEED_SLIDER.w * speedFraction;
    ctx.fillStyle = '#72f0c0';
    ctx.beginPath();
    ctx.arc(speedKnobX, 520, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('STRUCTURE SCALE', 62, 569);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText(`${formatSpeed(this.state.structureScale)}x`, 486, 569);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(STRUCTURE_SLIDER.x, 600);
    ctx.lineTo(STRUCTURE_SLIDER.x + STRUCTURE_SLIDER.w, 600);
    ctx.stroke();
    const structureFraction = structureScaleFraction(this.state.structureScale);
    const structureKnobX = STRUCTURE_SLIDER.x + STRUCTURE_SLIDER.w * structureFraction;
    ctx.fillStyle = '#d2a2ff';
    ctx.beginPath();
    ctx.arc(structureKnobX, 600, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('DISTANT BRIGHTNESS', 62, 649);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText(`${formatSpeed(this.state.distantBrightness)}x`, 486, 649);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(DISTANT_BRIGHTNESS_SLIDER.x, 680);
    ctx.lineTo(DISTANT_BRIGHTNESS_SLIDER.x + DISTANT_BRIGHTNESS_SLIDER.w, 680);
    ctx.stroke();
    const distantBrightnessFraction = (this.state.distantBrightness - 1) / 5;
    const distantBrightnessKnobX = DISTANT_BRIGHTNESS_SLIDER.x + DISTANT_BRIGHTNESS_SLIDER.w * distantBrightnessFraction;
    ctx.fillStyle = '#ffcf70';
    ctx.beginPath();
    ctx.arc(distantBrightnessKnobX, 680, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('XR & VISUAL', 62, 729);
    const guideToggles = [
      { bounds: TRAILS_TOGGLE, enabled: this.state.tracerTrailsEnabled, available: true, label: 'TRAILS' },
      { bounds: MARKERS_TOGGLE, enabled: this.state.gravityMarkersEnabled, available: true, label: 'MASSES' },
      { bounds: PASSTHROUGH_TOGGLE, enabled: this.state.passthroughEnabled, available: this.state.passthroughSupported, label: 'PASSTHRU' }
    ];
    for (const toggle of guideToggles) {
      ctx.fillStyle = !toggle.available
        ? 'rgba(101, 113, 141, .10)'
        : toggle.enabled ? 'rgba(114, 240, 192, .18)' : 'rgba(102, 157, 255, .10)';
      this.roundRect(toggle.bounds.x, toggle.bounds.y, toggle.bounds.w, toggle.bounds.h, 14);
      ctx.fill();
      ctx.strokeStyle = !toggle.available
        ? 'rgba(101, 113, 141, .25)'
        : toggle.enabled ? 'rgba(114, 240, 192, .7)' : 'rgba(111, 194, 255, .38)';
      ctx.lineWidth = 2;
      this.roundRect(toggle.bounds.x, toggle.bounds.y, toggle.bounds.w, toggle.bounds.h, 14);
      ctx.stroke();
      ctx.fillStyle = toggle.available ? '#f5f8ff' : '#65718d';
      ctx.font = '700 13px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const value = toggle.available ? (toggle.enabled ? 'ON' : 'OFF') : 'N/A';
      ctx.fillText(`${toggle.label}  ${value}`, toggle.bounds.x + toggle.bounds.w / 2, toggle.bounds.y + toggle.bounds.h / 2);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('CULL PARTICLES', 62, 809);
    ctx.fillText('DISTANCE', 220, 809);
    ctx.fillStyle = this.state.cullEnabled ? 'rgba(114, 240, 192, .18)' : 'rgba(255, 100, 125, .14)';
    this.roundRect(CULL_TOGGLE.x, CULL_TOGGLE.y, CULL_TOGGLE.w, CULL_TOGGLE.h, 14);
    ctx.fill();
    ctx.strokeStyle = this.state.cullEnabled ? 'rgba(114, 240, 192, .7)' : 'rgba(255, 125, 145, .6)';
    ctx.lineWidth = 2;
    this.roundRect(CULL_TOGGLE.x, CULL_TOGGLE.y, CULL_TOGGLE.w, CULL_TOGGLE.h, 14);
    ctx.stroke();
    ctx.fillStyle = '#f5f8ff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.state.cullEnabled ? 'ON' : 'OFF', CULL_TOGGLE.x + CULL_TOGGLE.w / 2, CULL_TOGGLE.y + CULL_TOGGLE.h / 2);
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = this.state.cullEnabled ? '#b9eaff' : '#65718d';
    ctx.textAlign = 'right';
    ctx.fillText(this.state.cullDistance.toLocaleString(), 486, 809);
    ctx.strokeStyle = this.state.cullEnabled ? 'rgba(111, 194, 255, .38)' : 'rgba(101, 113, 141, .25)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(CULL_SLIDER.x, 847);
    ctx.lineTo(CULL_SLIDER.x + CULL_SLIDER.w, 847);
    ctx.stroke();
    const cullFraction = (this.state.cullDistance - 100) / 49900;
    const cullKnobX = CULL_SLIDER.x + CULL_SLIDER.w * cullFraction;
    ctx.fillStyle = this.state.cullEnabled ? '#71d7ff' : '#65718d';
    ctx.beginPath();
    ctx.arc(cullKnobX, 847, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';

    for (const button of BUTTONS) {
      const label = button.action === 'pause' && this.state.paused
        ? 'RESUME'
        : button.action === 'recording'
          ? this.recordingButtonLabel()
          : button.label;
      const recordingButtonActive = button.action === 'recording' && this.state.recordingActive;
      ctx.fillStyle = button.action === 'exit' || recordingButtonActive
        ? 'rgba(255, 100, 125, .14)'
        : 'rgba(102, 157, 255, .14)';
      this.roundRect(button.x, button.y, button.w, button.h, 16);
      ctx.fill();
      ctx.strokeStyle = button.action === 'exit' || recordingButtonActive
        ? 'rgba(255, 125, 145, .65)'
        : 'rgba(111, 194, 255, .55)';
      ctx.lineWidth = 2;
      this.roundRect(button.x, button.y, button.w, button.h, 16);
      ctx.stroke();
      ctx.fillStyle = '#f5f8ff';
      ctx.font = button.action === 'recording' ? '700 14px Arial' : '700 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, button.x + button.w / 2, button.y + button.h / 2);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = this.state.recordingActive ? '#ff7d91' : this.state.recordingNotice ? '#ffcf70' : '#7785a5';
    ctx.font = this.state.recordingActive || this.state.recordingNotice ? '700 15px Arial' : '18px Arial';
    const footer = this.state.recordingActive
      ? `●  RECORDING ${formatDuration(this.state.recordingElapsedSeconds)}  ·  RECORD TO STOP`
      : this.state.recordingNotice || 'A: pause  ·  X: step  ·  Y: menu  ·  Triggers: fly';
    ctx.fillText(footer, 52, 1182, 444);
    this.texture.needsUpdate = true;
  }

  private recordingButtonLabel(): string {
    if (this.state.recordingActive) return `STOP ${formatDuration(this.state.recordingElapsedSeconds)}`;
    if (this.state.passthroughActive) return 'REC HELP';
    return this.state.recordingSupported ? 'RECORD' : 'REC N/A';
  }

  private hitAudio(x: number, y: number): boolean {
    if (inside(x, y, AUDIO_TOGGLE)) this.onAction('audioEnabled');
    else if (inside(x, y, VELOCITY_AUDIO_TOGGLE)) this.onAction('velocityAudioEnabled');
    else if (inside(x, y, MUSICAL_BUTTON)) this.onAction('audioMusical');
    else if (inside(x, y, SCIENTIFIC_BUTTON)) this.onAction('audioScientific');
    else if (inside(x, y, VELOCITY_TIMBRE_BUTTON)) this.onAction('velocityTimbre');
    else if (inside(x, y, AUDIO_VOLUME_SLIDER)) {
      this.onAction('audioVolume', THREE.MathUtils.clamp((x - AUDIO_VOLUME_SLIDER.x) / AUDIO_VOLUME_SLIDER.w, 0, 1));
    } else if (inside(x, y, AUDIO_RANGE_SLIDER)) {
      const fraction = THREE.MathUtils.clamp((x - AUDIO_RANGE_SLIDER.x) / AUDIO_RANGE_SLIDER.w, 0, 1);
      this.onAction('audioRange', Math.round(1 + fraction * 99));
    } else if (inside(x, y, AUDIO_RESPONSE_SLIDER)) {
      const fraction = THREE.MathUtils.clamp((x - AUDIO_RESPONSE_SLIDER.x) / AUDIO_RESPONSE_SLIDER.w, 0, 1);
      this.onAction('audioResponse', Math.round((0.05 + fraction * 1.95) * 20) / 20);
    } else if (inside(x, y, MUSICAL_MOVEMENT_SLIDER)) {
      this.onAction('musicalMovement', THREE.MathUtils.clamp((x - MUSICAL_MOVEMENT_SLIDER.x) / MUSICAL_MOVEMENT_SLIDER.w, 0, 1));
    } else if (inside(x, y, DISTANCE_AUDIO_TOGGLE)) {
      this.onAction('distanceAudioEnabled');
    } else if (inside(x, y, DISTANCE_AUDIO_MIX_SLIDER)) {
      this.onAction('distanceAudioMix', THREE.MathUtils.clamp((x - DISTANCE_AUDIO_MIX_SLIDER.x) / DISTANCE_AUDIO_MIX_SLIDER.w, 0, 1));
    } else if (inside(x, y, DISTANCE_AUDIO_RANGE_SLIDER)) {
      const fraction = THREE.MathUtils.clamp((x - DISTANCE_AUDIO_RANGE_SLIDER.x) / DISTANCE_AUDIO_RANGE_SLIDER.w, 0, 1);
      this.onAction('distanceAudioRange', Math.round(distanceRangeFromFraction(fraction) / 10) * 10);
    } else if (inside(x, y, AUDIO_DESIGN)) {
      this.page = 'design';
      this.draw();
    } else if (inside(x, y, AUDIO_RECORD)) {
      this.onAction('recording');
    } else if (inside(x, y, AUDIO_BACK)) {
      this.page = 'simulation';
      this.draw();
    } else if (inside(x, y, AUDIO_EXIT)) {
      this.onAction('exit');
    } else return false;
    return true;
  }

  private drawAudioPage(): void {
    const ctx = this.context;
    ctx.fillStyle = '#71d7ff';
    ctx.font = '700 22px Arial';
    ctx.fillText('GSPACE  /  AUDIO', 52, 60);
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 38px Arial';
    ctx.fillText('Velocity sonification', 52, 126, 444);
    ctx.fillStyle = '#98a6c8';
    ctx.font = '19px Arial';
    ctx.fillText('Eight velocity bands become eight voices', 52, 164);

    this.drawChoice(AUDIO_TOGGLE, `AUDIO  ${this.state.audioEnabled ? 'ON' : 'OFF'}`, this.state.audioEnabled);
    this.drawChoice(
      VELOCITY_AUDIO_TOGGLE,
      `VELOCITY  ${this.state.velocityAudioEnabled ? 'ON' : 'OFF'}`,
      this.state.velocityAudioEnabled
    );
    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('MODE  /  VELOCITY TIMBRE', 52, 294);
    this.drawChoice(MUSICAL_BUTTON, 'MUSICAL', this.state.audioMode === 'musical');
    this.drawChoice(SCIENTIFIC_BUTTON, 'SCIENTIFIC', this.state.audioMode === 'scientific');
    const timbreLabel = formatTimbre(this.state.velocityTimbre);
    this.drawChoice(VELOCITY_TIMBRE_BUTTON, timbreLabel, this.state.velocityTimbre !== 'sine');

    this.drawAudioSlider('VOLUME', `${Math.round(this.state.audioVolume * 100)}%`, AUDIO_VOLUME_SLIDER, this.state.audioVolume, '#72f0c0');
    this.drawAudioSlider('VELOCITY RANGE', String(this.state.audioRange), AUDIO_RANGE_SLIDER, (this.state.audioRange - 1) / 99, '#ff9b6a');
    this.drawAudioSlider('RESPONSE', `${this.state.audioResponse.toFixed(2)} s`, AUDIO_RESPONSE_SLIDER, (this.state.audioResponse - 0.05) / 1.95, '#d2a2ff');
    this.drawAudioSlider(
      'MUSICAL MOVEMENT', `${Math.round(this.state.musicalMovement * 100)}%`,
      MUSICAL_MOVEMENT_SLIDER, this.state.musicalMovement, '#72f0c0'
    );

    this.drawChoice(
      DISTANCE_AUDIO_TOGGLE,
      `NEAREST-MASS CHORD  ${this.state.distanceAudioEnabled ? 'ON' : 'OFF'}`,
      this.state.distanceAudioEnabled
    );
    this.drawAudioSlider(
      'DISTANCE MIX', `${Math.round(this.state.distanceAudioMix * 100)}%`,
      DISTANCE_AUDIO_MIX_SLIDER, this.state.distanceAudioMix, '#71d7ff'
    );
    this.drawAudioSlider(
      'DISTANCE RANGE', this.state.distanceAudioRange.toLocaleString(),
      DISTANCE_AUDIO_RANGE_SLIDER, distanceRangeFraction(this.state.distanceAudioRange), '#ffcf70'
    );

    this.drawChoice(AUDIO_BACK, 'SIMULATION', true);
    this.drawChoice(AUDIO_RECORD, this.recordingButtonLabel(), this.state.recordingActive, this.state.recordingActive);
    this.drawChoice(AUDIO_DESIGN, 'DESIGN', false);
    this.drawChoice(AUDIO_EXIT, 'EXIT XR', false, true);
  }

  private hitDesign(x: number, y: number): boolean {
    if (inside(x, y, DESIGN_VELOCITY_TIMBRE)) this.onAction('velocityTimbre');
    else if (inside(x, y, DESIGN_DISTANCE_TIMBRE)) this.onAction('distanceTimbre');
    else if (inside(x, y, DESIGN_ADAPTIVE)) this.onAction('adaptiveAudioRanges');
    else if (inside(x, y, DESIGN_EVENT_DENSITY)) {
      this.onAction('eventDensity', THREE.MathUtils.clamp((x - DESIGN_EVENT_DENSITY.x) / DESIGN_EVENT_DENSITY.w, 0, 1));
    } else if (inside(x, y, DESIGN_SPATIAL)) this.onAction('spatialSources');
    else if (inside(x, y, DESIGN_ACCENTS)) this.onAction('rareAccents');
    else if (inside(x, y, DESIGN_RECORD)) this.onAction('recording');
    else if (inside(x, y, DESIGN_BACK)) {
      this.page = 'simulation';
      this.draw();
    } else if (inside(x, y, DESIGN_AUDIO)) {
      this.page = 'audio';
      this.draw();
    } else if (inside(x, y, DESIGN_EXIT)) this.onAction('exit');
    else return false;
    return true;
  }

  private drawDesignPage(): void {
    const ctx = this.context;
    ctx.fillStyle = '#71d7ff';
    ctx.font = '700 22px Arial';
    ctx.fillText('GSPACE  /  SOUND DESIGN', 52, 60);
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 38px Arial';
    ctx.fillText('Physics-driven score', 52, 126, 444);
    ctx.fillStyle = '#98a6c8';
    ctx.font = '19px Arial';
    ctx.fillText(`Preset palette: ${this.state.audioPalette}`, 52, 164);

    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.fillText('VELOCITY TIMBRE  /  PRESS TO CYCLE', 52, 195);
    this.drawChoice(DESIGN_VELOCITY_TIMBRE, formatTimbre(this.state.velocityTimbre), this.state.velocityTimbre !== 'sine');
    ctx.fillStyle = '#98a6c8';
    ctx.fillText('DISTANCE TIMBRE  /  PRESS TO CYCLE', 52, 305);
    this.drawChoice(DESIGN_DISTANCE_TIMBRE, formatTimbre(this.state.distanceTimbre), this.state.distanceTimbre !== 'sine');

    this.drawChoice(
      DESIGN_ADAPTIVE,
      `ADAPTIVE RANGES  ${this.state.adaptiveAudioRanges ? 'ON' : 'OFF'}`,
      this.state.adaptiveAudioRanges
    );
    this.drawAudioSlider(
      'BELL DENSITY', `${Math.round(this.state.eventDensity * 100)}%`,
      DESIGN_EVENT_DENSITY, this.state.eventDensity, '#ffcf70'
    );
    this.drawChoice(
      DESIGN_SPATIAL,
      `SPATIAL SOURCES  ${this.state.spatialSourcesEnabled ? 'ON' : 'OFF'}`,
      this.state.spatialSourcesEnabled
    );
    this.drawChoice(
      DESIGN_ACCENTS,
      `RARE-EVENT ACCENTS  ${this.state.rareAccentsEnabled ? 'ON' : 'OFF'}`,
      this.state.rareAccentsEnabled
    );

    ctx.fillStyle = '#8291b2';
    ctx.font = '17px Arial';
    ctx.fillText('Bell notes remain tied to particles changing bands.', 52, 860, 444);
    ctx.fillText('Adaptive ranges keep all eight bands active over time.', 52, 892, 444);
    ctx.fillText('Accents mark close approaches and acceleration surges.', 52, 924, 444);

    this.drawChoice(DESIGN_BACK, 'SIMULATION', true);
    this.drawChoice(DESIGN_RECORD, this.recordingButtonLabel(), this.state.recordingActive, this.state.recordingActive);
    this.drawChoice(DESIGN_AUDIO, 'AUDIO', false);
    this.drawChoice(DESIGN_EXIT, 'EXIT XR', false, true);
  }

  private drawAudioSlider(label: string, value: string, bounds: typeof AUDIO_VOLUME_SLIDER, fraction: number, colour: string): void {
    const ctx = this.context;
    ctx.fillStyle = '#98a6c8';
    ctx.font = '700 16px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(label, bounds.x, bounds.y - 12);
    ctx.fillStyle = '#b9eaff';
    ctx.textAlign = 'right';
    ctx.fillText(value, bounds.x + bounds.w, bounds.y - 12);
    ctx.strokeStyle = 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(bounds.x, bounds.y + 24);
    ctx.lineTo(bounds.x + bounds.w, bounds.y + 24);
    ctx.stroke();
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.arc(bounds.x + bounds.w * THREE.MathUtils.clamp(fraction, 0, 1), bounds.y + 24, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.lineCap = 'butt';
  }

  private drawChoice(bounds: { x: number; y: number; w: number; h: number }, label: string, active: boolean, danger = false): void {
    const ctx = this.context;
    ctx.fillStyle = danger ? 'rgba(255, 100, 125, .14)' : active ? 'rgba(114, 240, 192, .18)' : 'rgba(102, 157, 255, .10)';
    this.roundRect(bounds.x, bounds.y, bounds.w, bounds.h, 14);
    ctx.fill();
    ctx.strokeStyle = danger ? 'rgba(255, 125, 145, .65)' : active ? 'rgba(114, 240, 192, .7)' : 'rgba(111, 194, 255, .38)';
    ctx.lineWidth = 2;
    this.roundRect(bounds.x, bounds.y, bounds.w, bounds.h, 14);
    ctx.stroke();
    ctx.fillStyle = '#f5f8ff';
    ctx.font = '700 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, bounds.x + bounds.w / 2, bounds.y + bounds.h / 2, bounds.w - 18);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const ctx = this.context;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
  }
}

function inside(x: number, y: number, bounds: { x: number; y: number; w: number; h: number }): boolean {
  return x >= bounds.x && x <= bounds.x + bounds.w && y >= bounds.y && y <= bounds.y + bounds.h;
}

function formatTimbre(value: string): string {
  const labels: Record<string, string> = {
    softPad: 'SOFT PAD', brightPad: 'BRIGHT PAD', deepSpace: 'DEEP SPACE', choir: 'CHOIR',
    shimmer: 'SHIMMER', glass: 'GLASS', darkDrone: 'DARK DRONE', warm: 'WARM PAD',
    deep: 'DEEP PAD', sine: 'PURE SINE'
  };
  return labels[value] ?? value.toUpperCase();
}

function formatSpeed(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2).replace(/0$/, '');
}

function formatDuration(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(wholeSeconds / 60);
  return `${minutes}:${String(wholeSeconds % 60).padStart(2, '0')}`;
}

function structureScaleFromFraction(fraction: number): number {
  return 0.5 * Math.pow(80, THREE.MathUtils.clamp(fraction, 0, 1));
}

function structureScaleFraction(value: number): number {
  return Math.log(THREE.MathUtils.clamp(value, 0.5, 40) / 0.5) / Math.log(80);
}

function distanceRangeFromFraction(fraction: number): number {
  return 10 * Math.pow(5000, THREE.MathUtils.clamp(fraction, 0, 1));
}

function distanceRangeFraction(value: number): number {
  return Math.log(THREE.MathUtils.clamp(value, 10, 50000) / 10) / Math.log(5000);
}
